import { createAsyncThunk } from "@reduxjs/toolkit";

import * as signals from "../../../store/signals";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../../../utils/ajax";
import { IBasket, ICustomerSummary } from "../models";

interface IStartAssistingCustomerResult {
    customer: ICustomerSummary;
    basket: IBasket;
}

/**
 * Starts assisting a customer as a CSR agent.
 *
 * This establishes a CSR session where the agent is "assisting" a specific customer.
 * The backend maintains this session state, allowing the CSR to act on behalf of the
 * customer for operations like basket management. After starting assistance, it fetches
 * the customer and basket details to display in the toolbar.
 *
 * Side effects:
 * - Emits `onBeforeStartAssistingCustomer` signal before starting
 * - Emits `onAfterStartAssistingCustomer` signal after completion
 * - These signals can be used by other components to react to assistance state changes
 *
 * @param userURL - The API URL of the customer to assist (e.g., "/api/csr/users/123/")
 * @returns Promise resolving to the customer and their current basket
 *
 * @example
 * dispatch(startAssistingCustomer(customer.url))
 */
export const startAssistingCustomer = createAsyncThunk(
    "csr/startAssistingCustomer",
    async (userURL: string): Promise<IStartAssistingCustomerResult> => {
        signals.csr.onBeforeStartAssistingCustomer.emit();
        await fetchJson(`${userURL}begin_assisting/`, {
            method: "POST",
            headers: {
                [CSRF_HEADER]: await getCSRFToken(),
            },
        });
        const [customer, basket] = await Promise.all([
            fetchJson<ICustomerSummary>(
                "/api/csr/users/currently_assisted_user/",
            ),
            fetchJson<IBasket>("/api/csr/baskets/currently_assisted_basket/"),
        ]);
        signals.csr.onAfterStartAssistingCustomer.emit();
        return {
            customer,
            basket,
        };
    },
);
